import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { GitCompareArrows, RefreshCw } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, formatDateTime, formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Field, InlineAlert, Input, NativeSelect, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, Skeleton, Textarea, useSave } from "@ncp/ui";

interface PlatformResult { key: string; label: string; labelAr: string; value: string }
interface RunPoint {
  id: string; key: string; label: string; labelAr?: string | null; platformValue: string; officersValue: string; officersSource?: string | null; recordedByName: string; recordedAt: string;
  divergent: boolean; ruling?: string | null; severity?: string | null; rulingReasons?: string | null; decidedByName?: string | null; decidedAt?: string | null; criticalRemaining: boolean;
}
interface RunFile { caseId: string; caseNumber: string; type: string; isTest: boolean; platform: PlatformResult[]; points: RunPoint[]; canRecord: boolean; canDecide: boolean }

const OUTCOMES = ["Approved", "ApprovedWithConditions", "Rejected", "Prohibited", "NoInfringement", "Infringement", "Exempted", "ExemptedWithConditions", "Withdrawn", "Closed", "GrievanceAccepted", "GrievanceRejected", "NotSubjectToNotification", "PrivateWarning", "AdministrativeFine", "ReferredToProsecution", "None"];
const RULINGS = ["Platform", "Officers", "Both", "Neither"] as const;
const SEVERITIES = ["Critical", "Major", "Minor"] as const;

/** The value of a point in the reader's words: an outcome, yes or no, a date, an amount or a number of days. */
function useValue() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return (key: string, value: string) => {
    if (value === "None") return t("backoffice:parallelRun.none");
    if (key === "outcome") return t(`decisionOutcomes.${value}`, { defaultValue: value });
    if (key === "notifiable") return t(`backoffice:parallelRun.notifiable.${value}`, { defaultValue: value });
    if (key === "deadline" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDate(value, locale);
    if (key === "fee" && !Number.isNaN(Number(value))) return `AED ${formatNumber(Number(value), locale)}`;
    if (key === "days" && !Number.isNaN(Number(value))) return `${formatNumber(Number(value), locale)} ${t("backoffice:statistics.units.days")}`;
    return value;
  };
}

/** The officers' own result on one point, in the shape the platform's result is compared with. */
function OfficersInput({ point, value, onChange }: { point: string; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  if (point === "outcome") return <NativeSelect value={value} onChange={(e) => onChange(e.target.value)} aria-label={t("backoffice:parallelRun.officers")}><option value="">—</option>{OUTCOMES.map((o) => <option key={o} value={o}>{o === "None" ? t("backoffice:parallelRun.none") : t(`decisionOutcomes.${o}`, { defaultValue: o })}</option>)}</NativeSelect>;
  if (point === "notifiable") return <NativeSelect value={value} onChange={(e) => onChange(e.target.value)} aria-label={t("backoffice:parallelRun.officers")}><option value="">—</option>{["Yes", "No"].map((o) => <option key={o} value={o}>{t(`backoffice:parallelRun.notifiable.${o}`)}</option>)}</NativeSelect>;
  if (point === "deadline") return <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} dir="ltr" aria-label={t("backoffice:parallelRun.officers")} />;
  return <Input type={point === "fee" || point === "days" ? "number" : "text"} value={value} onChange={(e) => onChange(e.target.value)} dir="auto" aria-label={t("backoffice:parallelRun.officers")} />;
}

/** The head of section's decision on one divergence: whose result is right and, where the platform's is wrong, how serious the error is. */
function DecideForm({ point, onDone }: { point: RunPoint; onDone: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const [ruling, setRuling] = useState("");
  const [severity, setSeverity] = useState("");
  const [reasons, setReasons] = useState("");
  const platformWrong = ruling === "Officers" || ruling === "Neither";
  const decide = useSave({
    mutationFn: () => api.post<RunPoint>(`/api/cases/parallel-run/points/${point.id}/decision`, { ruling, severity: platformWrong ? severity : null, reasons }),
    expect: "record",
    saved: t("backoffice:parallelRun.decided"),
    onSuccess: onDone,
  });
  return (
    <div className="mt-2 grid gap-2 rounded-lg border border-sand-200 bg-sand-50/60 p-3 md:grid-cols-2">
      <Field label={t("backoffice:parallelRun.ruling")} required>{(id) => <NativeSelect id={id} value={ruling} onChange={(e) => setRuling(e.target.value)}><option value="">—</option>{RULINGS.map((r) => <option key={r} value={r}>{t(`backoffice:parallelRun.rulings.${r}`)}</option>)}</NativeSelect>}</Field>
      {platformWrong ? <Field label={t("backoffice:parallelRun.severity")} hint={t("backoffice:parallelRun.severityHint")} required>{(id) => <NativeSelect id={id} value={severity} onChange={(e) => setSeverity(e.target.value)}><option value="">—</option>{SEVERITIES.map((s) => <option key={s} value={s}>{t(`backoffice:parallelRun.severities.${s}`)}</option>)}</NativeSelect>}</Field> : null}
      <Field label={t("backoffice:parallelRun.reasons")} required className="md:col-span-2">{(id) => <Textarea id={id} rows={3} value={reasons} onChange={(e) => setReasons(e.target.value)} draft={`parallel-run:${point.id}:decision`} dir="auto" />}</Field>
      <div className="md:col-span-2"><Button size="sm" variant="gold" disabled={!ruling || (platformWrong && !severity) || reasons.trim().length < 20} loading={decide.isPending} onClick={() => decide.mutate()}>{t("backoffice:parallelRun.decide")}</Button></div>
    </div>
  );
}

/**
 * Condition 1.10 on a file run alongside the Department's ordinary process: the platform's result on each point beside the officers' own
 * conclusion, every divergence for the head of section to decide, and nothing reaching the parties (the file is a test file).
 */
function RunBody({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const show = useValue();
  const file = useQuery({ queryKey: ["parallel-run", caseId], queryFn: () => api.get<RunFile>(`/api/cases/${caseId}/parallel-run`) });
  const [point, setPoint] = useState("outcome");
  const [officers, setOfficers] = useState("");
  const [source, setSource] = useState("");
  const [label, setLabel] = useState("");
  const [platformValue, setPlatformValue] = useState("");
  const [deciding, setDeciding] = useState<string | null>(null);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["parallel-run"] }); };
  const custom = point === "custom";
  const record = useSave({
    mutationFn: () => api.post<RunPoint>(`/api/cases/${caseId}/parallel-run/points`, { key: custom ? "custom" : point, officersValue: officers, officersSource: source || null, label: custom ? label : null, labelAr: null, platformValue: custom ? platformValue : null }),
    expect: "record",
    saved: (p) => (p.divergent ? t("backoffice:parallelRun.recordedDivergent") : t("backoffice:parallelRun.recordedAgree")),
    onSuccess: () => { setOfficers(""); setSource(""); setLabel(""); setPlatformValue(""); refresh(); },
  });
  const reread = useSave({
    mutationFn: (id: string) => api.post<RunPoint>(`/api/cases/parallel-run/points/${id}/refresh`, {}),
    expect: "record",
    saved: t("backoffice:parallelRun.reread"),
    onSuccess: refresh,
  });
  const f = file.data;
  if (file.isLoading || !f) return <Skeleton className="h-64" />;
  const label_ = (p: { label: string; labelAr?: string | null }) => (locale === "ar" && p.labelAr ? p.labelAr : p.label);
  return (
    <div className="space-y-5">
      {!f.isTest ? <InlineAlert tone="danger" title={t("backoffice:parallelRun.liveFile")}>{t("backoffice:parallelRun.liveFileHint")}</InlineAlert> : null}
      <section>
        <h4 className="mb-2 text-sm font-semibold">{t("backoffice:parallelRun.platform")}</h4>
        <ul className="grid gap-2 sm:grid-cols-2">
          {f.platform.map((r) => <li key={r.key} className="rounded-lg border border-sand-200 p-2.5 text-sm"><span className="block text-xs text-muted-foreground">{locale === "ar" ? r.labelAr : r.label}</span>{show(r.key, r.value)}</li>)}
        </ul>
      </section>
      <section>
        <h4 className="mb-2 text-sm font-semibold">{t("backoffice:parallelRun.points")}</h4>
        {f.points.length === 0 ? <p className="text-sm text-muted-foreground">{t("backoffice:parallelRun.noPoints")}</p> : (
          <ul className="space-y-2" data-testid="parallel-run-points">
            {f.points.map((p) => (
              <li key={p.id} className="rounded-lg border border-sand-200 p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium" dir="auto">{label_(p)}</span>
                  {p.divergent ? <Badge size="sm" variant={p.criticalRemaining ? "danger" : p.ruling ? "outline" : "warning"}>{p.ruling ? t(`backoffice:parallelRun.rulings.${p.ruling}`) : t("backoffice:parallelRun.divergent")}</Badge> : <Badge size="sm" variant="success">{p.key === "days" ? t("backoffice:parallelRun.measure") : t("backoffice:parallelRun.agree")}</Badge>}
                  {p.severity ? <Badge size="sm" variant={p.severity === "Critical" ? "danger" : "outline"}>{t(`backoffice:parallelRun.severities.${p.severity}`)}</Badge> : null}
                  <span className="ms-auto text-xs text-muted-foreground">{p.recordedByName} · {formatDateTime(p.recordedAt, locale)}</span>
                </div>
                <div className="mt-1 grid gap-2 sm:grid-cols-2">
                  <p><span className="block text-xs text-muted-foreground">{t("backoffice:parallelRun.platformResult")}</span>{show(p.key, p.platformValue)}</p>
                  <p><span className="block text-xs text-muted-foreground">{t("backoffice:parallelRun.officers")}{p.officersSource ? ` · ${p.officersSource}` : ""}</span>{show(p.key, p.officersValue)}</p>
                </div>
                {p.rulingReasons ? <p className="mt-1 text-xs text-muted-foreground" dir="auto">{t("backoffice:parallelRun.decidedBy", { name: p.decidedByName ?? "—" })}: {p.rulingReasons}</p> : null}
                <div className="mt-1 flex flex-wrap gap-2">
                  {p.divergent && f.canDecide && deciding !== p.id ? <Button size="sm" variant="outline" onClick={() => setDeciding(p.id)}>{t(p.ruling ? "backoffice:parallelRun.redecide" : "backoffice:parallelRun.decide")}</Button> : null}
                  {p.divergent && !p.key.startsWith("custom-") ? <Button size="sm" variant="ghost" loading={reread.isPending && reread.variables === p.id} onClick={() => reread.mutate(p.id)}><RefreshCw /> {t("backoffice:parallelRun.rereadAction")}</Button> : null}
                </div>
                {deciding === p.id ? <DecideForm point={p} onDone={() => { setDeciding(null); refresh(); }} /> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      {f.canRecord ? (
        <section className="space-y-2 rounded-lg border border-sand-200 bg-sand-50/60 p-3">
          <h4 className="text-sm font-semibold">{t("backoffice:parallelRun.record")}</h4>
          <div className="grid gap-2 md:grid-cols-2">
            <Field label={t("backoffice:parallelRun.point")}>{(id) => (
              <NativeSelect id={id} value={point} onChange={(e) => { setPoint(e.target.value); setOfficers(""); }}>
                {f.platform.map((r) => <option key={r.key} value={r.key}>{locale === "ar" ? r.labelAr : r.label}</option>)}
                <option value="custom">{t("backoffice:parallelRun.customPoint")}</option>
              </NativeSelect>
            )}</Field>
            <Field label={t("backoffice:parallelRun.officers")} required>{() => <OfficersInput point={point} value={officers} onChange={setOfficers} />}</Field>
            {custom ? <Field label={t("backoffice:parallelRun.customLabel")} required>{(id) => <Input id={id} value={label} onChange={(e) => setLabel(e.target.value)} dir="auto" />}</Field> : null}
            {custom ? <Field label={t("backoffice:parallelRun.customPlatform")} required>{(id) => <Input id={id} value={platformValue} onChange={(e) => setPlatformValue(e.target.value)} dir="auto" />}</Field> : null}
            <Field label={t("backoffice:parallelRun.source")} hint={t("backoffice:parallelRun.sourceHint")} className="md:col-span-2">{(id) => <Input id={id} value={source} onChange={(e) => setSource(e.target.value)} dir="auto" />}</Field>
          </div>
          <Button size="sm" variant="gold" disabled={!officers.trim() || (custom && (!label.trim() || !platformValue.trim()))} loading={record.isPending} onClick={() => record.mutate()}>{t("backoffice:parallelRun.recordAction")}</Button>
        </section>
      ) : null}
    </div>
  );
}

/** The parallel run's entry on a test file's header. */
export function ParallelRunButton({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const open = params.get("parallel") === "1";
  const setOpen = (o: boolean) => setParams((p) => { if (o) p.set("parallel", "1"); else p.delete("parallel"); return p; });
  return (
    <>
      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setOpen(true)}><GitCompareArrows /> {t("backoffice:parallelRun.title")}</Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="end" width="max-w-3xl">
          <SheetHeader><SheetTitle>{t("backoffice:parallelRun.title")}</SheetTitle><SheetDescription>{t("backoffice:parallelRun.hint")}</SheetDescription></SheetHeader>
          <SheetBody>{open ? <RunBody caseId={caseId} /> : null}</SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}
